// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, StateRow } from '../types';
import { rate, pct, times, delta, esc, COL } from '../format';
import { sparkline, tipAttr } from '../charts';
import { openState } from '../main';

interface Metric { id: string; label: string; get: (s: StateRow) => number | null; ref: number; fmt: (v: number | null) => string; higherWorse: boolean }

export function renderRankings(root: HTMLElement, data: Dataset): void {
  const c = data.meta.counts;
  const metrics: Metric[] = [
    { id: 'rate', label: 'Imprisonment rate', get: (s) => s.rateNow, ref: c.rate, fmt: (v) => rate(v), higherWorse: true },
    { id: 'atsi', label: 'Aboriginal & T.S.I. rate', get: (s) => s.atsiRateStd, ref: c.atsiRateStd, fmt: (v) => rate(v, 0), higherWorse: true },
    { id: 'ratio', label: 'Over-representation', get: (s) => s.ratioStd, ref: c.ratioStd, fmt: (v) => times(v), higherWorse: true },
    { id: 'remand', label: 'Remand share', get: (s) => s.remandShareNow, ref: c.remandShare, fmt: (v) => pct(v), higherWorse: true },
    { id: 'prior', label: 'Prior imprisonment', get: (s) => s.priorPctNow, ref: c.priorShare, fmt: (v) => pct(v), higherWorse: true },
    { id: 'growth', label: `Growth since ${data.meta.firstYear}`, get: (s) => s.growthPct, ref: c.growthSince2016Pct, fmt: (v) => delta(v), higherWorse: true },
  ];
  let metric = metrics[0];

  root.innerHTML = `
    <div class="view-head">
      <h2>States and territories, ranked</h2>
      <p>Every jurisdiction against the national figure, on whichever measure you choose. The dashed line marks the national value. Click any state for its full breakdown.</p>
    </div>
    <div class="controls">
      <span class="control-label">Rank by</span>
      <div class="seg" role="tablist" aria-label="Metric">
        ${metrics.map((mm) => `<button data-metric="${mm.id}" aria-pressed="${mm.id === metric.id}">${esc(mm.label)}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div id="rk-bars"></div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>All measures</h3><p>Imprisonment rate trend shown as a sparkline. Click a row to open the state.</p></div>
      <div class="table-scroll">
        <table>
          <thead><tr>
            <th>State / territory</th><th class="right">Rate</th><th class="right">Abor. &amp; T.S.I.</th>
            <th class="right">Over-rep</th><th class="right">Remand</th><th class="right">Growth</th><th>Rate trend</th>
          </tr></thead>
          <tbody id="rk-tbody"></tbody>
        </table>
      </div>
    </div>
  `;

  const bars = root.querySelector('#rk-bars') as HTMLElement;
  const draw = () => drawBars(bars, data, metric);
  draw();
  drawTable(root.querySelector('#rk-tbody') as HTMLElement, data);

  root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((b) =>
    b.addEventListener('click', () => {
      metric = metrics.find((mm) => mm.id === b.dataset.metric)!;
      root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      draw();
    }),
  );
}

function drawBars(el: HTMLElement, data: Dataset, metric: Metric): void {
  const rows = data.states.filter((s) => metric.get(s) != null).slice().sort((a, b) => (metric.get(b) as number) - (metric.get(a) as number));
  const vals = rows.map((s) => metric.get(s) as number);
  const max = Math.max(...vals, metric.ref, 0.0001);
  const min = Math.min(...vals, 0);
  const span = max - min || 1;
  const pos = (v: number) => ((v - min) / span) * 100;
  el.className = 'barlist';
  el.innerHTML = rows.map((s) => {
    const v = metric.get(s) as number;
    return `<div class="barrow" data-state="${s.code}" role="button" tabindex="0" aria-label="${esc(s.name)}: ${metric.fmt(v)}" ${tipAttr(`${s.name}\n${metric.label}: ${metric.fmt(v)}\nNational: ${metric.fmt(metric.ref)}`)}>
      <span class="lbl">${esc(s.name)}</span>
      <span class="track"><span style="width:${pos(v).toFixed(1)}%;background:${COL.total}"></span><span style="position:absolute;left:${pos(metric.ref).toFixed(1)}%;top:0;bottom:0;width:0;border-left:2px dashed var(--accent-warm)"></span></span>
      <span class="val">${metric.fmt(v)}</span>
    </div>`;
  }).join('') + `<p class="mini-note" style="margin-top:8px">Dashed line = national ${metric.fmt(metric.ref)}.</p>`;

  const go = (code: string) => openState(code);
  el.querySelectorAll<HTMLElement>('[data-state]').forEach((r) => {
    r.addEventListener('click', () => go(r.dataset.state!));
    r.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); go(r.dataset.state!); } });
  });
}

function drawTable(tbody: HTMLElement, data: Dataset): void {
  const rows = data.states.slice().sort((a, b) => (b.rateNow ?? 0) - (a.rateNow ?? 0));
  tbody.innerHTML = rows.map((s) => `
    <tr class="clickable" data-state="${s.code}" aria-label="Open ${esc(s.name)}">
      <td class="region-name">${esc(s.name)}</td>
      <td class="right num">${rate(s.rateNow)}</td>
      <td class="right num">${rate(s.atsiRateStd, 0)}</td>
      <td class="right num">${times(s.ratioStd)}</td>
      <td class="right num">${pct(s.remandShareNow)}</td>
      <td class="right num ${(s.growthPct ?? 0) >= 0 ? 'up' : 'down'}">${delta(s.growthPct)}</td>
      <td>${sparkline(s.totalRate, COL.total)}</td>
    </tr>`).join('');
  const go = (code: string) => openState(code);
  tbody.querySelectorAll<HTMLElement>('[data-state]').forEach((r) => r.addEventListener('click', () => go(r.dataset.state!)));
}
